/**
 * useInspectionLotSearch Hook
 * Provides debounced search functionality for inspection lots
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { BRRApiClient } from '../services/api';

interface UseInspectionLotSearchOptions {
  apiClient: BRRApiClient;
  query: string;
  debounceMs?: number;
  minQueryLength?: number;
}

interface InspectionLotResult {
  lotId: string;
}

interface UseInspectionLotSearchReturn {
  results: InspectionLotResult[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook that searches inspection lots with debouncing
 *
 * @example
 * const { results, loading, error } = useInspectionLotSearch({
 *   apiClient: brrApi,
 *   query: searchText,
 *   debounceMs: 500,
 *   minQueryLength: 3,
 * });
 */
export const useInspectionLotSearch = ({
  apiClient,
  query,
  debounceMs = 500,
  minQueryLength = 3,
}: UseInspectionLotSearchOptions): UseInspectionLotSearchReturn => {
  const [results, setResults] = useState<InspectionLotResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.searchInspectionLots(searchQuery);
      setResults(response.results);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, minQueryLength]);

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Return early if query is too short
    if (query.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    // Set new timeout
    setLoading(true);
    timeoutRef.current = window.setTimeout(() => {
      void search(query);
    }, debounceMs);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, debounceMs, minQueryLength, search]);

  return {
    results,
    loading,
    error
  };
};
